import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '../../../ui/Icon'
import { Button } from '../../../buttons/Button'
import { SummarySlotRow } from './summaryComponents/SummarySlotRow'
import MobileSummaryPanel from './mobileSummaryPanel/MobileSummaryPanel'
import { SummaryPricingDetails } from './summaryComponents/SummaryPricingDetails'
import ManagementPaymentMethodTabs from './summaryComponents/ManagementPaymentMethodTabs'

import { Money, SummaryLine } from '../../../../types/booking/booking'
import { PaymentMethod } from '../../../../types/payment/payment'

export type SummaryPanelProps = {
  lines: SummaryLine[]
  /** What is left to pay (after refund points); undefined while the price is unknown. */
  total?: Money
  /** Refund points paying part of the price, shown as their own line. */
  pointsAmount?: number
  onClear: () => void
  onConfirm: () => void
  confirmLabel: string
  showPrices?: boolean
  showPrepaid?: boolean
  totalSubtitle?: string
  confirmDisabled?: boolean
  loading?: boolean
  showConfirm?: boolean
  paymentMethod?: PaymentMethod
  onPaymentMethodChange?: (paymentMethod: PaymentMethod) => void
  /** Funding options (prepaid minutes, top-up pass) shown under the totals. */
  options?: ReactNode
}

export const isPrepaidLine = (line: SummaryLine) =>
  line.durationMinutes > 0 && line.prepaidMinutes >= line.durationMinutes

const SummaryPanel = ({
  lines,
  total,
  pointsAmount,
  onClear,
  onConfirm,
  confirmLabel,
  showPrices = true,
  showPrepaid = true,
  totalSubtitle,
  confirmDisabled = false,
  loading = false,
  showConfirm = true,
  paymentMethod,
  onPaymentMethodChange,
  options,
}: SummaryPanelProps) => {
  const { t } = useTranslation('bookings')
  const prepaidCount = lines.filter(isPrepaidLine).length
  const paidCount = lines.length - prepaidCount

  return (
    <>
      {lines.length > 0 && (
        <MobileSummaryPanel
          lines={lines}
          total={total}
          pointsAmount={pointsAmount}
          onClear={onClear}
          onConfirm={onConfirm}
          showPrices={showPrices}
          showPrepaid={showPrepaid}
          showConfirm={showConfirm}
          totalSubtitle={totalSubtitle}
          confirmLabel={confirmLabel}
          confirmDisabled={confirmDisabled}
          loading={loading}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={onPaymentMethodChange}
          prepaidCount={prepaidCount}
          paidCount={paidCount}
          options={options}
        />
      )}

      <div className="summary-card booking-summary-card">
        <div className="ph-row ph-between mb-8">
          <div>
            <div className="eyebrow">{t('booking.booking-courts.summary-panel.title')}</div>
            <h3 className="summary-title">
              {t('booking.booking-courts.summary-panel.selected-slots', {
                itemsLength: lines.length,
              })}
            </h3>
          </div>
          {lines.length > 0 && (
            <Button
              label={t('booking.booking-courts.summary-panel.clear-btn')}
              variant="ghost"
              size="sm"
              disabled={loading}
              onClick={onClear}
            />
          )}
        </div>

        {lines.length === 0 ? (
          <div className="empty mt-8">
            <Icon name="calendar" size={20} />
            <br />
            <span className="empty-text">{t('booking.booking-courts.summary-panel.pick-slots')}</span>
          </div>
        ) : (
          <>
            {lines.map(line => (
              <SummarySlotRow key={line.key} line={line} showPrices={showPrices} />
            ))}

            {showPrices && (
              <SummaryPricingDetails
                prepaidCount={prepaidCount}
                paidCount={paidCount}
                total={total}
                pointsAmount={pointsAmount}
                showPrepaid={showPrepaid}
                totalSubtitle={totalSubtitle}
              />
            )}

            {options}

            {paymentMethod && onPaymentMethodChange && (
              <div className="summary-payment-method-section">
                <ManagementPaymentMethodTabs
                  value={paymentMethod}
                  disabled={loading}
                  onChange={onPaymentMethodChange}
                />
              </div>
            )}

            {showConfirm && (
              <div className="summary-actions ph-col mt-16">
                <Button
                  label={confirmLabel}
                  icon={<Icon name="arrowR" size={16} />}
                  iconPosition="right"
                  size="lg"
                  block
                  loading={loading}
                  disabled={confirmDisabled || loading}
                  onClick={onConfirm}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default SummaryPanel
